<template>
  <div>
    <div>
      <TheListHeader :title="$t('pages.review.reviewAnalyticsPage.title')" />
      <div class="mb-6 flex flex-wrap items-center gap-2 px-6 sm:px-12">
        <ThePageSettings
          v-model:date-range-value="dateValue"
          display-store-selector
          display-date-range-picker
          display-filters
          @store-selected="changeStore($event)"
          @update:date-range-value="dateRangeChanged"
          @validate-filters="updateFilters"
        >
          <template #filtersDescription>
            {{ $t('pages.review.reviewPage.filters.description') }}
          </template>
          <template #filtersContent>
            <fieldset>
              <legend class="mb-4 text-base font-medium text-foreground">
                {{ $t('pages.review.reviewPage.filters.platforms') }}
              </legend>
              <AppCheckboxField
                v-for="platform in platforms"
                :id="`platforms[${platform.slug}]`"
                :key="platform"
                v-model="selected.platforms"
                :value="platform.slug"
                class="my-2"
                :label="
                  platform.slug !== 'google'
                    ? platform.name
                    : $t('pages.review.reviewPage.filters.byDefault', { platform: platform.name })
                "
              />
            </fieldset>
          </template>
        </ThePageSettings>
      </div>

      <div class="px-6 pb-6 sm:px-12 sm:pb-12">
        <div class="second-row flex justify-between">
          <div class="w-full">
            <UpsellModuleBanner />

            <dl v-if="isLoaded" class="grid grid-cols-1 gap-5 xl:grid-cols-3">
              <AppStatsCard :title="$t('pages.review.reviewAnalyticsPage.cards.averageNote')">
                <span v-if="!showFacebookDescription">
                  {{ ratingAverage }}
                </span>
                <FacebookReviewDescription v-else />
              </AppStatsCard>
              <AppStatsCard
                :title="$t('pages.review.reviewAnalyticsPage.cards.ratingAverage')"
                :variation="showFacebookDescription ? null : ratingAverageInThisPeriodTrend"
              >
                <span v-if="!showFacebookDescription">
                  {{ ratingAverageInThisPeriod }}
                </span>
                <FacebookReviewDescription v-else />
              </AppStatsCard>
              <AppStatsCard
                :title="$t('pages.review.reviewAnalyticsPage.cards.totalOpinion')"
                :variation="countTotalReviewsTrend"
              >
                {{ formatNumber(countTotalReviews) }}
              </AppStatsCard>
            </dl>
            <!-- BEGIN 1st row titles desktop -->
            <div class="mt-10 hidden grid-cols-1 gap-0 xl:grid xl:grid-cols-3 xl:gap-5">
              <div class="col-span-2">
                <ChartTitle :title="$t('pages.review.reviewAnalyticsPage.countReviewsByRatingByMonth.title')">
                  <template #icon-left>
                    <BaseIcon name="HeroChatBubbleOvalLeftEllipsisIconSolid" />
                  </template>
                </ChartTitle>
              </div>
              <div v-if="isReviewFetchingInRealTime" class="relative right-0 top-0 col-span-1">
                <ChartTitle :title="$t('pages.review.reviewAnalyticsPage.treatmentRate.title')" :has-icon-right="true">
                  <template #icon-left>
                    <BaseIcon name="HeroClipboardDocumentCheckIconSolid" />
                  </template>
                  <template #icon-right>
                    <div v-if="displayRatePercentageAlert" class="relative">
                      <RatePercentageAlert />
                    </div>
                  </template>
                </ChartTitle>
              </div>
            </div>
            <!-- END 1st row titles desktop -->

            <!-- BEGIN 1st row graph -->
            <div class="mb-5 mt-10 grid grid-cols-1 gap-0 xl:mt-0 xl:grid-cols-3 xl:gap-5">
              <ChartTitle
                class="col-span-3 xl:hidden"
                :title="$t('pages.review.reviewAnalyticsPage.countReviewsByRatingByMonth.title')"
              >
                <template #icon-left>
                  <BaseIcon name="HeroChatBubbleOvalLeftEllipsisIconSolid" />
                </template>
              </ChartTitle>
              <div class="my-5 rounded-lg border bg-white px-6 py-5 sm:col-span-2">
                <p class="mt-1 px-6 pb-6 text-center text-sm font-light text-muted-foreground sm:text-left">
                  {{ $t('pages.review.reviewAnalyticsPage.countReviewsByRatingByMonth.description') }}
                </p>
                <div>
                  <ReviewsByRatingByMonthChart v-if="isLoaded" />
                  <div v-else class="relative">
                    <div class="flex items-center justify-center" placeholder-animation>
                      <BaseIcon
                        name="HeroChartBarIconSolid"
                        class="relative my-2 mt-1 h-48 w-48 overflow-hidden text-foreground-disabled md:my-6 md:h-72 md:w-72"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <ChartTitle
                v-if="isReviewFetchingInRealTime"
                class="col-span-3 mt-5 xl:mt-0 xl:hidden"
                :title="$t('pages.review.reviewAnalyticsPage.treatmentRate.title')"
                :has-icon-right="true"
              >
                <template #icon-left>
                  <BaseIcon name="HeroClipboardDocumentCheckIconSolid" />
                </template>
                <template #icon-right>
                  <div v-if="displayRatePercentageAlert" class="relative">
                    <RatePercentageAlert />
                  </div>
                </template>
              </ChartTitle>
              <div v-if="isReviewFetchingInRealTime" class="my-5 rounded-lg border bg-white px-6 py-5">
                <p class="mt-1 px-6 pb-2 text-center text-sm font-light text-muted-foreground sm:text-left">
                  {{ $t('pages.review.reviewAnalyticsPage.treatmentRate.description') }}
                </p>
                <TreatmentRateChart v-if="isLoaded" :percentage="treatmentRatePercentage" />
                <div v-else class="relative pt-6">
                  <div class="flex items-center justify-center" placeholder-animation>
                    <BaseIcon
                      name="HeroChartPieIconSolid"
                      class="relative my-2 mt-1 h-48 w-48 overflow-hidden text-foreground-disabled md:my-6 md:h-72 md:w-72"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- END 1st row graph -->

            <!-- BEGIN 2nd row graph -->
            <ChartTitle :title="$t('pages.review.reviewAnalyticsPage.ratingAverageByMonth.title')">
              <template #icon-left>
                <BaseIcon name="HeroStarIconSolid" />
              </template>
            </ChartTitle>
            <div class="my-5 rounded-lg border bg-white px-6 py-5">
              <div>
                <RatingAverageByMonthChart v-if="isLoaded" />
                <div v-else class="relative">
                  <div class="flex items-center justify-center" placeholder-animation>
                    <BaseIcon
                      name="HeroPresentationChartLineIconSolid"
                      class="relative my-2 mt-1 h-48 w-48 overflow-hidden text-foreground-disabled md:my-6 md:h-72 md:w-72"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- END 2nd row graph -->

            <!-- BEGIN 3rd row titles desktop -->
            <div class="mt-10 hidden grid-cols-1 gap-0 xl:grid xl:grid-cols-3 xl:gap-5">
              <div class="col-span-2">
                <ChartTitle :title="$t('pages.review.reviewAnalyticsPage.answerTimeAverageByMonth.title')">
                  <template #icon-left>
                    <BaseIcon name="HeroClockIconSolid" />
                  </template>
                </ChartTitle>
              </div>
              <div class="col-span-1">
                <ChartTitle :title="$t('pages.review.reviewAnalyticsPage.reviewsDistributionByPlatform.title')">
                  <template #icon-left>
                    <BaseIcon name="HeroSquare2StackIconSolid" />
                  </template>
                </ChartTitle>
              </div>
            </div>
            <!-- END 3rd row titles desktop -->

            <!-- BEGIN 3rd row graph -->
            <div class="my-5 grid grid-cols-1 gap-0 xl:mt-0 xl:grid-cols-3 xl:gap-5">
              <ChartTitle
                :title="$t('pages.review.reviewAnalyticsPage.answerTimeAverageByMonth.title')"
                class="col-span-3 mt-5 xl:mt-0 xl:hidden"
              >
                <template #icon-left>
                  <BaseIcon name="HeroClockIconSolid" />
                </template>
              </ChartTitle>
              <div class="my-5 rounded-lg border bg-white px-6 py-5 sm:col-span-2">
                <div>
                  <AnswerTimeAverageByMonth v-if="isLoaded" />
                  <div v-else class="relative">
                    <div class="flex items-center justify-center" placeholder-animation>
                      <BaseIcon
                        name="HeroPresentationChartLineIconSolid"
                        class="relative my-2 mt-1 h-48 w-48 overflow-hidden text-foreground-disabled md:my-6 md:h-72 md:w-72"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <ChartTitle
                :title="$t('pages.review.reviewAnalyticsPage.reviewsDistributionByPlatform.title')"
                class="col-span-3 mt-5 xl:mt-0 xl:hidden"
              >
                <template #icon-left>
                  <BaseIcon name="HeroSquare2StackIconSolid" />
                </template>
              </ChartTitle>
              <div class="my-5 rounded-lg border bg-white px-6 py-5 align-middle">
                <ReviewsDistributionByPlatform v-if="isLoaded" />
                <div v-else class="relative">
                  <div class="flex items-center justify-center" placeholder-animation>
                    <BaseIcon
                      name="HeroChartPieIconSolid"
                      class="relative my-2 mt-1 h-48 w-48 overflow-hidden text-foreground-disabled md:my-6 md:h-72 md:w-72"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- END 3rd row graph -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ThePageSettings from '@/components/layout/ThePageSettings.vue'
import ReviewsByRatingByMonthChart from './ReviewAnalyticsPage/ReviewsByRatingByMonthChart.vue'
import TreatmentRateChart from './ReviewAnalyticsPage/TreatmentRateChart.vue'
import RatePercentageAlert from './ReviewAnalyticsPage/RatePercentageAlert.vue'
import RatingAverageByMonthChart from './ReviewAnalyticsPage/RatingAverageByMonthChart.vue'
import AnswerTimeAverageByMonth from './ReviewAnalyticsPage/AnswerTimeAverageByMonth.vue'
import ReviewsDistributionByPlatform from './ReviewAnalyticsPage/ReviewsDistributionByPlatform.vue'
import AppCheckboxField from '@/components/ui/AppCheckboxField.vue'
import ChartTitle from '@/components/ui/ChartTitle.vue'
import FacebookReviewDescription from './ReviewAnalyticsPage/FacebookReviewDescription.vue'
import AppStatsCard from '@/components/ui/AppStatsCard.vue'
import useLocalStorage from '@/composables/useLocalStorage'
import useStore from '@/composables/store'
import useNumber from '@/composables/number'
import useReview from '@/composables/review'
import UpsellModuleBanner from '@/components/UpsellModuleBanner.vue'

const defaultSelected = {
  platforms: ['google']
}

export default {
  components: {
    AppStatsCard,
    ThePageSettings,
    ReviewsByRatingByMonthChart,
    TreatmentRateChart,
    RatingAverageByMonthChart,
    AnswerTimeAverageByMonth,
    ReviewsDistributionByPlatform,
    AppCheckboxField,
    ChartTitle,
    RatePercentageAlert,
    FacebookReviewDescription,
    UpsellModuleBanner
  },
  setup() {
    const { permissions, stores, moduleAccessesByStores } = useLocalStorage()
    const { selectedStore, selectStore } = useStore()
    const { roundNumber, formatNumber } = useNumber()
    const { fetchCharts, isLoaded, selected, dateValue, reviewStatistics, isReviewFetchingInRealTime } = useReview()
    const router = useRouter()

    return {
      permissions,
      selectedStore,
      selectStore,
      roundNumber,
      formatNumber,
      fetchCharts,
      isReviewFetchingInRealTime,
      reviewStatistics,
      isLoaded,
      selected,
      dateValue,
      stores,
      moduleAccessesByStores,
      router
    }
  },
  data: () => {
    return {
      defaultSelected: defaultSelected,
      currentSelected: defaultSelected,
      platforms: []
    }
  },
  computed: {
    showFacebookDescription() {
      return this.currentSelected.platforms.length === 1 && this.currentSelected.platforms.includes('facebook')
    },
    formattedPlatforms() {
      const currentPlatforms = this.currentSelected.platforms
      if (this.platforms.length !== currentPlatforms.length) {
        return `(${currentPlatforms.join(', ')})`
      } else {
        return ''
      }
    },
    filtered() {
      return JSON.stringify(this.currentSelected) != JSON.stringify(this.defaultSelected)
    },
    treatmentRatePercentage() {
      const reviewStatistics = this.reviewStatistics
      if (reviewStatistics.reviewsReplied > 0 && reviewStatistics.reviewsNotReplied > 0) {
        const percentage =
          (reviewStatistics.reviewsReplied / (reviewStatistics.reviewsNotReplied + reviewStatistics.reviewsReplied)) *
          100
        return parseFloat(Number(percentage).toFixed(0))
      } else {
        return reviewStatistics.reviewsReplied > 0 ? 100 : 0
      }
    },
    displayRatePercentageAlert() {
      const reviewStatistics = this.reviewStatistics
      return reviewStatistics.reviewsNotReplied && this.treatmentRatePercentage < 50
    },
    ratingAverage() {
      if (this.reviewStatistics.ratingAverage) {
        return this.roundNumber(this.reviewStatistics.ratingAverage, 1)
      } else {
        return 0
      }
    },
    ratingAverageInThisPeriod() {
      return this.roundNumber(this.reviewStatistics.ratingAverageInThisPeriod, 1)
    },
    ratingAverageInThisPeriodTrend() {
      if (this.reviewStatistics.ratingAverageTrend) {
        return this.roundNumber(this.reviewStatistics.ratingAverageTrend, 1)
      } else {
        return 0
      }
    },
    countTotalReviews() {
      return this.reviewStatistics.countTotalReviews
    },
    countTotalReviewsTrend() {
      if (this.reviewStatistics.countTotalReviewsTrend) {
        return this.roundNumber(this.reviewStatistics.countTotalReviewsTrend, 1)
      } else {
        return 0
      }
    },
    crumbs() {
      return [
        {
          name: this.$t('pages.review.reviewAnalyticsPage.title')
        }
      ]
    },
    storesWithEReputation() {
      return this.stores.filter((store) => this.moduleAccessesByStores[store.id]?.includes('e-reputation'))
    }
  },
  async mounted() {
    const { data } = await this.axios.get('/review-platforms')
    this.platforms = data.data
    await this.fetchCharts()
    this.isLoaded = true
  },
  methods: {
    changeFilters(e) {
      this.selected[e.name] = []
      document.querySelectorAll(`input[name=${e.name}]:checked`).forEach((el) => {
        this.selected[e.name].push(el.value)
      })
    },
    changeStore(id) {
      if (this.storesWithEReputation.some((store) => store.id === id)) {
        this.selectStore(0)
        this.router.push({ name: 'EReputation', params: { page: 'statistics' } })
        return
      }

      this.fetchCharts()
    },
    updateFilters() {
      if (this.selected.platforms.length === 0) {
        this.selected.platforms.push('google')
        this.currentSelected = JSON.parse(JSON.stringify(this.selected))
      } else {
        this.currentSelected = JSON.parse(JSON.stringify(this.selected))
      }
      this.fetchCharts()
    },
    dateRangeChanged() {
      this.fetchCharts()
    }
  }
}
</script>
